import React, { useState } from 'react';
import { DollarSign } from 'lucide-react';
import SingleRangeSlider from '../common/SingleRangeSlider';
import TuitionFeeReferenceModal from '../Modal/TuitionFeeReferenceModal';
import type { ParentRegistrationFormData } from '../../types';
import { useOptionalI18n } from '@/i18n';
import { localizeText } from '@/i18n/options';
import { useLocalizedPath } from '@/i18n/navigation';

interface BudgetStepProps {
  formData: ParentRegistrationFormData;
  errors: Record<string, string>;
  onChange: (formData: Partial<ParentRegistrationFormData>) => void;
  showTerms?: boolean;
}

const BudgetStep: React.FC<BudgetStepProps> = ({
  formData,
  errors,
  onChange,
  showTerms = true,
}) => {
  const { locale } = useOptionalI18n();
  const [showFeeModal, setShowFeeModal] = useState(false);
  const localizePath = useLocalizedPath();

  return (
    <>
    <TuitionFeeReferenceModal isOpen={showFeeModal} onClose={() => setShowFeeModal(false)} />
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-1 h-5 bg-accent rounded-full" />
        <h3 className="text-lg font-semibold text-gray-900">{localizeText(locale, 'Budget')}</h3>
      </div>
      <div className="space-y-4">
        <div className="bg-white p-5 rounded-xl border border-gray-200">
          <p className="text-sm text-gray-600 mb-4">
            {localizeText(locale, 'Set your maximum hourly rate budget for this tuition case. This helps tutors understand your budget expectations.')}
          </p>

          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700 flex items-center">
              <DollarSign className="h-4 w-4 mr-1" />
              {localizeText(locale, 'Maximum Hourly Rate')}
            </span>
            <button
              type="button"
              onClick={() => setShowFeeModal(true)}
              className="flex items-center gap-1 text-xs font-medium text-secondary hover:text-primary transition-colors underline underline-offset-2"
            >
              {localizeText(locale, 'View Rate Reference')}
            </button>
          </div>
          <SingleRangeSlider
            min={10}
            max={800}
            value={formData.hourlyRate || 50}
            onChange={(value) => onChange({ hourlyRate: value })}
            unit="RM"
          />
          {errors.hourlyRate && (
            <p className="text-red-500 text-sm mt-2" role="alert">{errors.hourlyRate}</p>
          )}

          <div className="mt-6 p-4 bg-primary-50 border border-primary-100 rounded-xl">
            <p className="text-sm text-primary">
              <strong>{localizeText(locale, 'Tip:')}</strong> {localizeText(locale, 'Setting a competitive hourly rate increases the chances of finding qualified tutors quickly.')}
            </p>
          </div>
        </div>

        {showTerms && (
          <div className="border-t pt-4">
            <div className="flex items-start space-x-3 p-4 bg-secondary-50 border border-secondary-200 rounded-xl">
              <input
                type="checkbox"
                id="parent-terms"
                checked={formData.termsAccepted}
                onChange={(e) => onChange({ termsAccepted: e.target.checked })}
                className="w-5 h-5 mt-0.5 cursor-pointer"
              />
              <label htmlFor="parent-terms" className="text-sm text-gray-700 cursor-pointer">
                {localizeText(locale, 'I agree to the')}{' '}
                <a
                  href={localizePath('/terms-customers')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-secondary underline hover:text-secondary-dark"
                >
                  {localizeText(locale, 'Terms of Service')}
                </a>{' '}
                {localizeText(locale, 'and')}{' '}
                <a
                  href={localizePath('/privacy-policy')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-secondary underline hover:text-secondary-dark"
                >
                  {localizeText(locale, 'Privacy Policy')}
                </a>
                {localizeText(locale, '. I confirm that all information provided is accurate and truthful.')}{' '}
                <span className="text-red-500">*</span>
              </label>
            </div>
            {errors.termsAccepted && (
              <p className="text-red-500 text-sm mt-1" role="alert">{errors.termsAccepted}</p>
            )}
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default BudgetStep;
